import type { LocaleCode } from '@/i18n/locales'
import { isLocaleCode } from '@/i18n/locales'
import type { AgeBand, Gender } from '@/profile/types'
import { ageToBand } from '@/profile/types'
import { parseSpokenNumber } from './numbers'
import { normalize, stripLeadIn, stripTrailing, titleCase, words } from './normalize'

/**
 * Turning what someone said into a profile value.
 *
 * These are deliberately plain, deterministic functions rather than a model
 * call: they run offline, they cost nothing, they are unit-testable, and when
 * they get something wrong the reason is readable. The assistant reads low
 * confidence values back for confirmation, so the cost of a near miss is one
 * extra exchange rather than a wrong answer saved forever.
 */

export interface Extraction<T> {
  value: T | null
  /** 0..1, independent of the recogniser's own confidence. */
  confidence: number
  /** What was actually heard, kept for display and for diagnostics. */
  raw: string
}

function miss(raw: string): Extraction<never> {
  return { value: null, confidence: 0, raw }
}

// ---------------------------------------------------------------------------
// Yes / no / skip
// ---------------------------------------------------------------------------

export type Affirmation = 'yes' | 'no' | 'skip'

const AFFIRMATIONS: Record<LocaleCode, Record<Affirmation, readonly string[]>> = {
  en: {
    yes: ['yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'correct', 'right', 'that\'s right', 'go ahead', 'i agree', 'agreed', 'please do', 'fine'],
    /**
     * Bare negators like "not", "don't" and "do not" are deliberately absent.
     *
     * They appear inside plenty of answers that are not refusals: "I do not
     * know" is a hesitation, "I'm not sure" is a request to be asked again.
     * Matching on them turned both into a permanent no at the consent
     * question. Only phrases that can carry a refusal on their own belong here.
     */
    no: [
      'no',
      'nope',
      'nah',
      'wrong',
      'incorrect',
      "that's wrong",
      'no thanks',
      'disagree',
      'do not save',
      "don't save",
      'not right',
    ],
    skip: ['skip', 'pass', 'next', 'prefer not', 'rather not', 'not say', 'no comment', 'move on'],
  },
  ha: {
    yes: [
      'ee',
      'eh',
      "na'am",
      'to',
      'toh',
      'haka ne',
      'daidai',
      'i',
      'shi ke nan',
      'na yarda',
      'kwarai',
      'lallai',
      'madalla',
      "ba shakka",
      'babu matsala',
      'ba laifi',
      'ba komai',
    ],
    /**
     * The bare negation particle `ba` is deliberately absent.
     *
     * Hausa marks negation with a clause particle, not a standalone word, and
     * it appears in plenty of affirmative phrases: `ba shakka` is "certainly",
     * `ba komai` is "no problem", `ba laifi` is "that is fine". Listing bare
     * `ba` made every one of those read as a refusal, and at the consent
     * question that meant a user saying an emphatic yes was told their answers
     * would not be saved. Only the bracketing `ba ... ba` forms belong here.
     */
    no: ["a'a", 'aa', 'ba haka ba', 'ba daidai ba', 'ban yarda ba', 'kada'],
    skip: ['tsallake', 'wuce', 'ba na so', 'bar shi', 'gaba'],
  },
}

/**
 * Answers that are neither yes, no, nor a request to skip.
 *
 * Checked first, and they return nothing so the question is asked again.
 * Without this, "I am not sure" matched on the `sure` in the yes list and was
 * recorded as an agreement, which at the consent question is the worst
 * possible place to guess.
 */
const HESITATIONS: Record<LocaleCode, readonly string[]> = {
  en: ['not sure', 'unsure', 'not certain', 'do not know', "don't know", 'dunno', 'maybe'],
  ha: ['ban sani ba', 'ban tabbata ba', 'watakila'],
}

/**
 * Reads a yes, a no, or a request to skip.
 *
 * Longer phrases are checked before single words so "ba daidai ba" is not read
 * as the bare "ba", and negations are checked before affirmations so "that's
 * wrong" does not match on "right".
 */
export function extractAffirmation(input: string, locale: LocaleCode): Extraction<Affirmation> {
  const raw = input
  const text = normalize(input)
  if (text.length === 0) return miss(raw)

  for (const hesitation of HESITATIONS[locale]) {
    if (text.includes(normalize(hesitation))) return miss(raw)
  }

  const table = AFFIRMATIONS[locale]
  const tokens = words(text)

  const candidates: Array<{ kind: Affirmation; phrase: string }> = []
  for (const kind of ['skip', 'no', 'yes'] as const) {
    for (const phrase of table[kind]) {
      candidates.push({ kind, phrase: normalize(phrase) })
    }
  }
  candidates.sort((a, b) => b.phrase.length - a.phrase.length)

  for (const candidate of candidates) {
    if (candidate.phrase.includes(' ')) {
      if (text.includes(candidate.phrase)) {
        return { value: candidate.kind, confidence: 0.95, raw }
      }
    } else if (tokens.includes(candidate.phrase)) {
      // A bare "yes" alone is unambiguous; buried in a sentence it is weaker.
      const confidence = tokens.length === 1 ? 0.97 : 0.8
      return { value: candidate.kind, confidence, raw }
    }
  }

  return miss(raw)
}

// ---------------------------------------------------------------------------
// Language
// ---------------------------------------------------------------------------

const LANGUAGE_WORDS: Record<LocaleCode, readonly string[]> = {
  en: ['english', 'ingilishi', 'turanci', 'anglais'],
  ha: ['hausa', 'hausanci', 'haoussa'],
}

/** Language choice, understood no matter which language it is said in. */
export function extractLanguage(input: string): Extraction<LocaleCode> {
  const raw = input
  const text = normalize(input)
  if (text.length === 0) return miss(raw)

  for (const [code, spellings] of Object.entries(LANGUAGE_WORDS)) {
    if (!isLocaleCode(code)) continue
    for (const spelling of spellings) {
      if (text.includes(spelling)) return { value: code, confidence: 0.95, raw }
    }
  }
  return miss(raw)
}

// ---------------------------------------------------------------------------
// Name
// ---------------------------------------------------------------------------

const NAME_LEAD_INS: Record<LocaleCode, readonly string[]> = {
  en: [
    'my name is',
    'my name',
    'the name is',
    'i am called',
    'they call me',
    'you can call me',
    'call me',
    'i am',
    "i'm",
    'it is',
    "it's",
    'this is',
    'name',
  ],
  ha: [
    'sunana',
    'suna na',
    'ana kira na',
    'ana kirana',
    'ka kira ni',
    'ni ne',
    'ni ce',
    'ni',
  ],
}

/**
 * Grammatical tails that a prefix-stripper cannot reach.
 *
 * Hausa puts the equative copula at the end of the clause, so "Ni Amina ce"
 * carries `ce` after the name and "a Kano nake zaune" carries two words after
 * the place. Without stripping these, the profile remembers someone as
 * "Amina Ce" and their home as "Kano Nake Zaune", both at high enough
 * confidence to skip the read-back that would have caught it.
 */
const TRAILING_COPULA: Record<LocaleCode, readonly string[]> = {
  /*
   * English has no equative tail, but it does leave one behind.
   *
   * "Call me Amina, I am 34" carries the opening of a second answer after the
   * name, and the number in it is dropped before this runs, which leaves the
   * pronoun and the verb dangling on the end. Without them the name is stored
   * as "Amina I Am", at a confidence high enough to skip the read-back that
   * would have caught it. Only ever removed from the end, so a sentence that
   * merely contains the words is untouched.
   */
  en: ['i', 'am', "i'm"],
  ha: ['ne', 'ce', 'nake', 'kake', 'take', 'zaune', 'ke'],
}

/**
 * Prepositions that mean the rest of the sentence is a different answer.
 *
 * "I am Amina from Niamey" strips to three tokens, which is short enough to
 * look like a name, so it was stored whole and every later session opened
 * "Welcome back, Amina From Niamey". The parser already knows `from` marks a
 * place; it just was not using that when reading a name.
 */
const NAME_BOUNDARIES: Record<LocaleCode, readonly string[]> = {
  en: ['from', 'in', 'at', 'and'],
  ha: ['daga', 'a', 'kuma'],
}

/** Words that mean the transcript is a refusal, not a name. */
const NON_NAMES = new Set([
  'skip',
  'nothing',
  'none',
  'no',
  'yes',
  'tsallake',
  'ba',
  "a'a",
  'ee',
])

const MAX_NAME_WORDS = 3

/** A token that is only digits. Nobody is called 34. */
const NUMERIC = /^\d+$/

/**
 * Finds where the name actually starts, which is not always the beginning.
 *
 * `stripLeadIn` only removes a wrapper sitting at the front of the utterance,
 * and that is not where it is in the sentence this product asks people to say.
 * "I am 62, call me Amina" is the example `runner.ts` uses for reading a
 * volunteered fact and an answer out of one turn, and it has two wrappers in
 * it: the age's and the name's. Stripping only the first leaves "62, call me
 * Amina", which is cut at no boundary, is four tokens long, and comes back as
 * "62 Call" at a confidence low enough to be thrown away. The person said
 * their name clearly and was asked for it again.
 *
 * So every lead-in is searched for anywhere in the utterance and the last one
 * wins, because the last framing is the one introducing the name. A candidate
 * whose remainder is nothing but digits is skipped: "my name is Amina, I am
 * 34" ends with the age's wrapper, and taking that one would answer the name
 * question with a number.
 */
function afterLastLeadIn(normalized: string, leadIns: readonly string[]): string | null {
  // Word boundaries on both sides. Without them "in" matches inside a name and
  // the lead-in appears to start halfway through a word.
  const padded = ` ${normalized} `
  let bestEnd = -1
  let rest: string | null = null

  for (const leadIn of leadIns) {
    const candidate = normalize(leadIn)
    if (candidate === '') continue

    const at = padded.lastIndexOf(` ${candidate} `)
    if (at < 0) continue

    /*
     * Compared by where the lead-in ends, not by where it starts.
     *
     * The list contains phrases nested inside other phrases: `name` sits
     * inside `my name is` and starts three characters later than it does, so
     * choosing by start position picked the shorter one and answered "my name
     * is Amina Bello" with "Is Amina Bello". Where the wrapper ends is where
     * the name begins, which is the thing actually being looked for.
     */
    const end = at + candidate.length + 1
    if (end <= bestEnd) continue

    const remainder = padded.slice(end).trim()
    if (remainder === '') continue
    if (words(remainder).every((token) => NUMERIC.test(token))) continue

    bestEnd = end
    rest = remainder
  }

  return rest
}

/**
 * Pulls a name out of an answer, dropping the conversational wrapper.
 *
 * Confidence drops with length, because a long transcript usually means the
 * recogniser heard a sentence rather than a name, and it is better to ask
 * again than to greet someone as "Sorry I Think My Name".
 */
export function extractName(input: string, locale: LocaleCode): Extraction<string> {
  const raw = input
  const normalized = normalize(input)
  const leadIns = NAME_LEAD_INS[locale]
  const wrapped = afterLastLeadIn(normalized, leadIns) ?? stripLeadIn(input, leadIns)

  /*
   * Numbers are dropped outright rather than read back, and before the tail is
   * trimmed rather than after.
   *
   * The read-back exists to catch a mishearing, not a category error, and "34.
   * Have I got that right?" is the assistant proposing to call somebody by
   * their age. It reached the read-back at 0.68, which is high enough to be
   * kept by somebody who is not listening carefully. Dropping the number first
   * is also what leaves "Amina I am" ending in a word `stripTrailing` knows
   * about rather than in a digit it does not.
   */
  const withoutNumbers = words(wrapped)
    .filter((token) => !NUMERIC.test(token))
    .join(' ')
  const stripped = stripTrailing(withoutNumbers, TRAILING_COPULA[locale])

  let tokens = words(stripped).filter((token) => !NON_NAMES.has(token))
  if (tokens.length === 0) return miss(raw)

  // "I am Amina from Niamey" answers two questions at once. Cut at the
  // preposition and keep only the name, rather than storing the whole phrase.
  const boundary = tokens.findIndex((token) => NAME_BOUNDARIES[locale].includes(token))
  const cutAtBoundary = boundary > 0
  if (cutAtBoundary) tokens = tokens.slice(0, boundary)
  if (tokens.length === 0) return miss(raw)

  if (tokens.length > MAX_NAME_WORDS) {
    // Too much text to be a name, but the first word or two might still be it.
    // Report it at low confidence so the assistant reads it back.
    const guess = titleCase(tokens.slice(0, 2).join(' '))
    return { value: guess, confidence: 0.35, raw }
  }

  const value = titleCase(tokens.join(' '))
  const usedLeadIn = normalize(stripped) !== normalize(input)
  // "My name is Amina" is the strongest signal. A bare single word is still a
  // perfectly normal answer to "what is your name", so it is trusted enough to
  // skip the read-back; two or more bare words is more likely a fragment of a
  // sentence the recogniser mangled, so that gets read back.
  const base = usedLeadIn ? 0.92 : tokens.length === 1 ? 0.82 : 0.7
  // A sentence that had to be cut is worth reading back, since the cut is a
  // guess about which half was the name.
  const confidence = cutAtBoundary ? Math.min(base, 0.68) : base
  return { value, confidence, raw }
}

// ---------------------------------------------------------------------------
// Region
// ---------------------------------------------------------------------------

const REGION_LEAD_INS: Record<LocaleCode, readonly string[]> = {
  en: [
    'i live in',
    'i live at',
    'i am from',
    "i'm from",
    'i am in',
    "i'm in",
    'i stay in',
    'i reside in',
    'from',
    'in',
    'at',
  ],
  ha: ['ina zaune a', 'na zauna a', 'ina zaune', 'daga', 'a', 'garin', 'birnin'],
}

const MAX_REGION_WORDS = 5

export function extractRegion(input: string, locale: LocaleCode): Extraction<string> {
  const raw = input
  const stripped = stripTrailing(
    stripLeadIn(input, REGION_LEAD_INS[locale]),
    TRAILING_COPULA[locale],
  )
  const tokens = words(stripped).filter((token) => !NON_NAMES.has(token))
  if (tokens.length === 0) return miss(raw)
  if (tokens.length > MAX_REGION_WORDS) {
    return { value: titleCase(tokens.slice(0, 3).join(' ')), confidence: 0.4, raw }
  }

  const usedLeadIn = normalize(stripped) !== normalize(input)
  // Answering "where do you live" with just "Niamey" is the normal case, not a
  // sign of uncertainty. Charging it a confidence penalty made the assistant
  // read back almost every region answer, which is exactly the kind of
  // needless repetition that makes a voice product feel broken.
  const confidence = usedLeadIn ? 0.9 : tokens.length <= 2 ? 0.85 : 0.72
  return { value: titleCase(tokens.join(' ')), confidence, raw }
}

// ---------------------------------------------------------------------------
// Age
// ---------------------------------------------------------------------------

const AGE_BAND_PHRASES: Record<LocaleCode, ReadonlyArray<readonly [string, AgeBand]>> = {
  en: [
    ['under eighteen', 'under18'],
    ['under 18', 'under18'],
    ['teenager', 'under18'],
    ['teens', 'under18'],
    ['twenties', '25-34'],
    ['early twenties', '18-24'],
    ['late twenties', '25-34'],
    ['thirties', '25-34'],
    ['late thirties', '35-49'],
    ['forties', '35-49'],
    ['fifties', '50-64'],
    ['sixties', '50-64'],
    ['retired', '65plus'],
    ['over sixty five', '65plus'],
  ],
  ha: [
    ['kasa da goma sha takwas', 'under18'],
    ['yaro', 'under18'],
    ['matashi', '18-24'],
    ['tsoho', '65plus'],
  ],
}

/**
 * Age answers are messy: "27", "twenty seven", "I'm in my thirties",
 * "ashirin da bakwai". A stated number is banded; a vague phrase maps to the
 * band it most likely means, at lower confidence.
 */
export function extractAgeBand(input: string, locale: LocaleCode): Extraction<AgeBand> {
  const raw = input
  const text = normalize(input)
  if (text.length === 0) return miss(raw)

  // Longest phrase first so "early twenties" beats "twenties".
  const phrases = [...AGE_BAND_PHRASES[locale]].sort((a, b) => b[0].length - a[0].length)
  for (const [phrase, band] of phrases) {
    if (text.includes(normalize(phrase))) return { value: band, confidence: 0.7, raw }
  }

  const age = parseSpokenNumber(text, locale)
  if (age !== null) {
    const band = ageToBand(age)
    if (band) return { value: band, confidence: 0.93, raw }
  }

  return miss(raw)
}

// ---------------------------------------------------------------------------
// Gender
// ---------------------------------------------------------------------------

const GENDER_WORDS: Record<LocaleCode, ReadonlyArray<readonly [string, Gender]>> = {
  en: [
    ['prefer not to say', 'undisclosed'],
    ['rather not say', 'undisclosed'],
    ['not say', 'undisclosed'],
    ['non binary', 'other'],
    ['nonbinary', 'other'],
    ['non-binary', 'other'],
    ['woman', 'woman'],
    ['female', 'woman'],
    ['girl', 'woman'],
    ['lady', 'woman'],
    ['she', 'woman'],
    ['man', 'man'],
    ['male', 'man'],
    ['boy', 'man'],
    ['guy', 'man'],
    ['he', 'man'],
    ['other', 'other'],
  ],
  ha: [
    ['ba na so in fadi', 'undisclosed'],
    ['ba na so', 'undisclosed'],
    ['mace', 'woman'],
    ['mata', 'woman'],
    ['yarinya', 'woman'],
    ['namiji', 'man'],
    ['maza', 'man'],
    ['saurayi', 'man'],
    ['wani', 'other'],
  ],
}

export function extractGender(input: string, locale: LocaleCode): Extraction<Gender> {
  const raw = input
  const text = normalize(input)
  if (text.length === 0) return miss(raw)

  const skip = extractAffirmation(input, locale)
  if (skip.value === 'skip') return { value: 'undisclosed', confidence: 0.95, raw }

  const entries = [...GENDER_WORDS[locale]].sort((a, b) => b[0].length - a[0].length)
  const tokens = words(text)
  for (const [phrase, gender] of entries) {
    const normalized = normalize(phrase)
    const matched = normalized.includes(' ') ? text.includes(normalized) : tokens.includes(normalized)
    if (matched) return { value: gender, confidence: 0.9, raw }
  }

  return miss(raw)
}

// ---------------------------------------------------------------------------
// Interests
// ---------------------------------------------------------------------------

const INTEREST_LEAD_INS: Record<LocaleCode, readonly string[]> = {
  en: [
    'i am interested in',
    "i'm interested in",
    'i like',
    'i love',
    'i enjoy',
    'i follow',
    'interested in',
    'my interests are',
    'my interests',
  ],
  ha: ["ina sha'awar", "ina son", "sha'awata", "ina sha'awa"],
}

const INTEREST_SEPARATORS: Record<LocaleCode, readonly string[]> = {
  en: [' and ', ' also ', ' plus ', ' with '],
  ha: [' da ', ' kuma ', ' tare da '],
}

const INTEREST_STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'some',
  'lot',
  'lots',
  'of',
  'like',
  'things',
  'thing',
  'stuff',
  'abubuwa',
  'abu',
  'wasu',
  'kamar',
])

const MAX_INTERESTS = 5

/**
 * Splits an answer into separate interests. English joins with "and", Hausa
 * with "da", and both use commas when the recogniser adds them.
 */
export function extractInterests(input: string, locale: LocaleCode): Extraction<string[]> {
  const raw = input
  if (normalize(input).length === 0) return miss(raw)

  // Commas have to be split before normalisation, which strips punctuation.
  // Only the first segment can carry the lead-in phrase.
  const segments = input.split(/[,;]/)
  let parts = segments
    .map((segment, index) =>
      index === 0 ? stripLeadIn(segment, INTEREST_LEAD_INS[locale]) : normalize(segment),
    )
    .filter((segment) => segment.length > 0)

  for (const separator of INTEREST_SEPARATORS[locale]) {
    parts = parts.flatMap((part) => part.split(separator))
  }

  const items = parts
    .map((part) =>
      words(part)
        .filter((token) => !INTEREST_STOPWORDS.has(token))
        .join(' '),
    )
    .filter((part) => part.length > 1)
    .slice(0, MAX_INTERESTS)
    .map((part) => titleCase(part))

  if (items.length === 0) return miss(raw)

  const usedLeadIn = (parts[0] ?? '') !== normalize(segments[0] ?? '')
  return { value: items, confidence: usedLeadIn ? 0.88 : 0.76, raw }
}
